import { Component, ElementRef, inject, input, OnInit, Renderer2 } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { CustomDialogService } from '@core/services/custom-dialog.service';
import { KlImageCropperDialogComponent } from '../kl-image-cropper-dialog/kl-image-cropper-dialog';
import { resolveBootstrapColWidths } from '../../utils/bootstrap-col-widths';

/** Material-style image field: base64 in form control (no data: prefix), matches legacy API. */
@Component({
  selector: 'kl-image-field',
  standalone: false,
  templateUrl: './kl-image-field.html',
  styleUrl: './kl-image-field.scss',
})
export class KlImageFieldComponent implements OnInit {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly customDialog = inject(CustomDialogService);
  private readonly translate = inject(TranslateService);

  readonly formGroup = input.required<FormGroup>();
  readonly controlName = input('image');
  readonly label = input('');
  readonly disabled = input(false);

  readonly col = input('12');
  readonly sm = input<string | undefined>(undefined);
  readonly md = input<string | undefined>(undefined);
  readonly lg = input<string | undefined>(undefined);
  readonly xl = input<string | undefined>(undefined);
  readonly marginBottom = input(true);

  fieldId(): string {
    return `kl-img-${this.controlName()}`.replace(/\s/g, '_');
  }

  ngOnInit(): void {
    const el = this.host.nativeElement;
    const col = this.col();
    const { sm, md, lg, xl } = resolveBootstrapColWidths(col, this.sm(), this.md(), this.lg(), this.xl());
    this.renderer.addClass(el, 'col-' + col);
    this.renderer.addClass(el, 'col-sm-' + sm);
    this.renderer.addClass(el, 'col-md-' + md);
    this.renderer.addClass(el, 'col-lg-' + lg);
    this.renderer.addClass(el, 'col-xl-' + xl);
  }

  /** Read from the control on every check, so values patched with `emitEvent: false` still show. */
  previewUrl(): string | null {
    return this.toPreviewUrl(this.formGroup().get(this.controlName())?.value);
  }

  openPreviewDialog(requestUploadOnOpen = false): void {
    if (this.disabled()) return;
    const src = this.previewUrl();

    const modalId = `kl-image-cropper-${this.controlName()}`;
    const ref = this.customDialog.open({
      modalId,
      component: KlImageCropperDialogComponent,
      header: this.translate.instant('image_resize'),
      width: 'min(72rem, 90vw)',
      contentStyle: { overflow: 'hidden' },
      data: { imageDataUrl: src ?? '', requestUploadOnOpen },
    });

    ref?.onClose.pipe(take(1)).subscribe((result: unknown) => {
      if (typeof result !== 'string' || result.trim() === '') {
        return;
      }
      const base64 = result.includes(',') ? (result.split(',')[1] ?? '') : result;
      this.formGroup().get(this.controlName())?.setValue(base64);
      this.formGroup().get(this.controlName())?.markAsDirty();
    });
  }

  clearImage(): void {
    if (this.disabled()) return;
    this.formGroup().get(this.controlName())?.setValue('');
    this.formGroup().get(this.controlName())?.markAsDirty();
  }

  private toPreviewUrl(value: unknown): string | null {
    if (value == null) return null;
    const s = String(value).trim();
    if (!s) return null;
    if (s.startsWith('data:')) return s;
    return `data:image/jpeg;base64,${s}`;
  }
}
